import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { authApi, links } from '@/lib/erpApi';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Loader2, UserCircle2, Phone, MessageCircle, Send, Mail, Search } from 'lucide-react';

const blank = {
  name: '', father_name: '', photo: '', mobile: '', whatsapp: '', alt_mobile: '', email: '',
  occupation: '', current_address: '', permanent_address: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  aadhaar_number: '', aadhaar_doc: '', pan_number: '', pan_doc: '', id_proof_doc: '',
  police_verification_doc: '', form_submitted: false, notes: '', is_active: true,
};

/**
 * Tenants — full-profile CRUD with one-click Call/WhatsApp/SMS/Email deep links.
 * KYC docs will get file-upload UI in Phase 2.
 */
export default function TenantsPage() {
  const { token } = useAuth();
  const api = authApi(token);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/erp/tenants', { params: search ? { search } : {} })
      .then(({ data }) => setRows(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [search]);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...blank, ...t }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) return toast.error('Name and mobile are required');
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      if (editing) {
        await api.put(`/erp/tenants/${editing.id}`, payload);
        toast.success('Tenant updated');
      } else {
        await api.post('/erp/tenants', payload);
        toast.success('Tenant created');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete tenant "${t.name}"?`)) return;
    try {
      await api.delete(`/erp/tenants/${t.id}`);
      toast.success('Deleted'); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const wappMsg = (t) => `Namaste ${t.name} ji, Shree Bajrang Developers se sampark kar rahe hain.`;

  return (
    <div className="space-y-6" data-testid="erp-tenants-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Tenants</h1>
          <p className="text-gray-500 text-sm mt-1">Rental tenant KYC + contacts. One-click Call / WhatsApp / SMS / Email.</p>
        </div>
        <Button onClick={openNew} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-tenant-btn">
          <Plus className="h-4 w-4 mr-2" /> New Tenant
        </Button>
      </div>

      <Card><CardContent className="p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input placeholder="Search name / mobile / email / Aadhaar" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </CardContent></Card>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand-orange" /></div>
      ) : rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-gray-500">No tenants yet.</CardContent></Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((t) => (
            <Card key={t.id} data-testid={`tenant-card-${t.mobile}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-full">
                    <UserCircle2 className="h-6 w-6 text-brand-orange" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading font-semibold text-gray-900 truncate">{t.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{t.occupation || '—'}</p>
                  </div>
                </div>
                <div className="mt-3 text-sm text-gray-700 space-y-0.5">
                  <p>📞 {t.mobile}</p>
                  {t.email && <p className="truncate">✉️ {t.email}</p>}
                  {t.aadhaar_number && <p>🆔 {t.aadhaar_number}</p>}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <a href={links.tel(t.mobile)}><Button size="sm" variant="outline"><Phone className="h-3.5 w-3.5" /></Button></a>
                  <a href={links.whatsapp(t.whatsapp || t.mobile, wappMsg(t))} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><MessageCircle className="h-3.5 w-3.5 text-emerald-600" /></Button></a>
                  <a href={links.sms(t.mobile, wappMsg(t))}><Button size="sm" variant="outline"><Send className="h-3.5 w-3.5" /></Button></a>
                  {t.email && <a href={links.mail(t.email, 'Shree Bajrang Developers', '')}><Button size="sm" variant="outline"><Mail className="h-3.5 w-3.5" /></Button></a>}
                  <span className="grow" />
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)}><Edit className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(t)}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                {t.form_submitted && <Badge className="mt-3 bg-emerald-100 text-emerald-800">Form Submitted</Badge>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="tenant-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Tenant' : 'New Tenant'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="tenant-name" /></div>
              <div className="space-y-1"><Label>Father's Name</Label><Input value={form.father_name || ''} onChange={(e) => setForm({ ...form, father_name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Mobile *</Label><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="tenant-mobile" /></div>
              <div className="space-y-1"><Label>WhatsApp</Label><Input value={form.whatsapp || ''} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="Defaults to mobile if empty" /></div>
              <div className="space-y-1"><Label>Alternate Mobile</Label><Input value={form.alt_mobile || ''} onChange={(e) => setForm({ ...form, alt_mobile: e.target.value })} /></div>
              <div className="space-y-1"><Label>Email</Label><Input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1"><Label>Occupation</Label><Input value={form.occupation || ''} onChange={(e) => setForm({ ...form, occupation: e.target.value })} /></div>
              <div className="space-y-1"><Label>Photo URL</Label><Input value={form.photo || ''} onChange={(e) => setForm({ ...form, photo: e.target.value })} placeholder="Upload UI in Phase 2" /></div>

              <div className="space-y-1 md:col-span-2 pt-2 border-t"><Label className="text-xs uppercase tracking-wider text-gray-500">Addresses</Label></div>
              <div className="space-y-1 md:col-span-2"><Label>Current Address</Label><Textarea rows={2} value={form.current_address || ''} onChange={(e) => setForm({ ...form, current_address: e.target.value })} /></div>
              <div className="space-y-1 md:col-span-2"><Label>Permanent Address</Label><Textarea rows={2} value={form.permanent_address || ''} onChange={(e) => setForm({ ...form, permanent_address: e.target.value })} /></div>

              <div className="space-y-1 md:col-span-2 pt-2 border-t"><Label className="text-xs uppercase tracking-wider text-gray-500">Emergency Contact</Label></div>
              <div className="space-y-1"><Label>Name</Label><Input value={form.emergency_contact_name || ''} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} /></div>
              <div className="space-y-1"><Label>Phone</Label><Input value={form.emergency_contact_phone || ''} onChange={(e) => setForm({ ...form, emergency_contact_phone: e.target.value })} /></div>

              <div className="space-y-1 md:col-span-2 pt-2 border-t"><Label className="text-xs uppercase tracking-wider text-gray-500">KYC</Label></div>
              <div className="space-y-1"><Label>Aadhaar Number</Label><Input value={form.aadhaar_number || ''} onChange={(e) => setForm({ ...form, aadhaar_number: e.target.value })} /></div>
              <div className="space-y-1"><Label>PAN Number</Label><Input value={form.pan_number || ''} onChange={(e) => setForm({ ...form, pan_number: e.target.value })} /></div>

              <div className="space-y-1 md:col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <label className="flex items-center gap-2 md:col-span-2 text-sm">
                <input type="checkbox" checked={form.form_submitted} onChange={(e) => setForm({ ...form, form_submitted: e.target.checked })} />
                Rental Form Submitted
              </label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-gradient-to-r from-brand-orange to-brand-rust text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
